import type { CreateStaffRequest, PlatformRole } from '@afra/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'
import { RequestError, api } from '../../lib/api'
import { ROLE_FA, STATUS_FA, faDate } from './labels'

/**
 * Afra's own accounts.
 *
 * Staff are created with a password rather than invited: the list of people who
 * can see every charity should be one somebody deliberately added a name to,
 * not something a forwarded code can join.
 */
export function StaffTab({ canManage }: { canManage: boolean }) {
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const staff = useQuery({
    queryKey: ['admin-staff'],
    queryFn: () => api.adminUsers({ staffOnly: true }),
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-staff'] })
    queryClient.invalidateQueries({ queryKey: ['admin-users'] })
  }
  const fail = (err: unknown, fallback: string) =>
    setError(err instanceof RequestError ? err.messageFa : fallback)

  const create = useMutation({
    mutationFn: (input: CreateStaffRequest) => api.adminCreateStaff(input),
    onSuccess: (result) => {
      setError(null)
      setNotice(`${result.user.email} به کارشناسان افرا اضافه شد.`)
      refresh()
    },
    onError: (err) => fail(err, 'کارشناس اضافه نشد.'),
  })

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'active' | 'disabled' }) =>
      api.adminSetUserStatus(id, status),
    onSuccess: () => {
      setError(null)
      refresh()
    },
    onError: (err) => fail(err, 'تغییر وضعیت انجام نشد.'),
  })

  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: number; role: PlatformRole }) => api.adminSetUserRole(id, role),
    onSuccess: () => {
      setError(null)
      refresh()
    },
    onError: (err) => fail(err, 'نقش عوض نشد.'),
  })

  return (
    <div className="stack">
      {error && <p className="notice notice--error">{error}</p>}
      {notice && <p className="notice notice--teach">{notice}</p>}

      {canManage && <AppointCard pending={create.isPending} onSubmit={(i) => create.mutate(i)} />}

      <div className="card">
        <h2>کارشناسان افرا</h2>
        <p className="meta">
          کارشناس همهٔ مؤسسه‌ها را می‌بیند. مدیر ارشد علاوه بر آن می‌تواند به‌جای یک مؤسسه وارد
          شود، کارشناس تعریف کند و دسترسی بدهد.
        </p>

        {staff.isLoading && <p className="muted">در حال بارگذاری…</p>}

        {staff.data && (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>حساب</th>
                  <th>نقش</th>
                  <th>آخرین ورود</th>
                  <th>وضعیت</th>
                  {canManage && <th />}
                </tr>
              </thead>
              <tbody>
                {staff.data.users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="ltr">{user.email}</div>
                      <div className="meta">{user.displayName ?? 'بدون نام'}</div>
                    </td>
                    <td>{ROLE_FA[user.role]}</td>
                    <td>{faDate(user.lastLoginOn)}</td>
                    <td>
                      <span
                        className={user.status === 'active' ? 'badge badge--good' : 'badge badge--bad'}
                      >
                        {STATUS_FA[user.status] ?? user.status}
                      </span>
                    </td>
                    {canManage && (
                      <td>
                        <div className="table__actions">
                          <select
                            aria-label={`نقش ${user.email}`}
                            value={user.role}
                            disabled={setRole.isPending}
                            onChange={(e) => setRole.mutate({ id: user.id, role: e.target.value as PlatformRole })}
                          >
                            <option value="afra_admin">{ROLE_FA.afra_admin}</option>
                            <option value="super_admin">{ROLE_FA.super_admin}</option>
                            <option value="assessor">{ROLE_FA.assessor}</option>
                          </select>
                          <button
                            type="button"
                            className="btn btn--quiet btn--sm"
                            onClick={() =>
                              setStatus.mutate({
                                id: user.id,
                                status: user.status === 'active' ? 'disabled' : 'active',
                              })
                            }
                          >
                            {user.status === 'active' ? 'غیرفعال کردن' : 'فعال کردن'}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function AppointCard({
  pending,
  onSubmit,
}: {
  pending: boolean
  onSubmit: (input: CreateStaffRequest) => void
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [role, setRole] = useState<PlatformRole>('afra_admin')

  const submit = (event: FormEvent) => {
    event.preventDefault()
    onSubmit({ email, password, displayName: displayName.trim() || undefined, role })
    setEmail('')
    setPassword('')
    setDisplayName('')
  }

  return (
    <div className="card">
      <h2>تعریف کارشناس تازه</h2>
      <form onSubmit={submit}>
        <div className="grid-2">
          <div className="field">
            <label htmlFor="staff-email">ایمیل</label>
            <input
              id="staff-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="staff-password">گذرواژه</label>
            <input
              id="staff-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="دست‌کم ۱۲ نویسه"
            />
          </div>
          <div className="field">
            <label htmlFor="staff-name">نام</label>
            <input
              id="staff-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="staff-role">نقش</label>
            <select
              id="staff-role"
              value={role}
              onChange={(e) => setRole(e.target.value as PlatformRole)}
            >
              <option value="afra_admin">{ROLE_FA.afra_admin}</option>
              <option value="super_admin">{ROLE_FA.super_admin}</option>
              <option value="assessor">{ROLE_FA.assessor}</option>
            </select>
          </div>
        </div>
        <button
          type="submit"
          className="btn"
          disabled={pending || !email.includes('@') || password.length < 12}
        >
          {pending ? 'در حال ثبت…' : 'اضافه کن'}
        </button>
      </form>
    </div>
  )
}
